import { useMemo, useState, useEffect } from 'react';
import { useOrderDelays } from './useOrderableMenu';

export type AvailabilityBannerType = 
  | 'none' 
  | 'opens-soon' 
  | 'closes-soon' 
  | 'closed' 
  | 'order-after-close'
  | 'scheduled-unavailable'
  | 'scheduled';

export interface OrderAvailabilityResult {
  isAvailable: boolean;
  bannerType: AvailabilityBannerType;
  bannerMessage: string;
  nextAvailableTime?: Date;
  expectedPickupTime: Date;
  isLoading: boolean;
}

interface LiveStatus {
  open: boolean;
  nextOpenAt?: number;
  nextCloseAt?: number;
  message?: string;
}

interface UseOrderAvailabilityParams {
  restaurantId: string | undefined;
  liveStatus?: LiveStatus | undefined;
  serviceType: 'delivery' | 'takeout';
  timing: 'asap' | 'scheduled';
  scheduledStart?: number;
  orderingEnabled?: boolean;
  /** For ASAP orders: indicates if the menu/asap endpoint returned items (true = available) */
  menuFetchSuccess?: boolean;
  /** For ASAP orders: indicates if the menu fetch is still loading */
  menuFetchLoading?: boolean;
  /** Restaurant timezone for formatting times (e.g., 'America/Denver') */
  timezone?: string;
}

const OPENS_SOON_THRESHOLD_MS = 20 * 60 * 1000;
const CLOSES_SOON_THRESHOLD_MS = 30 * 60 * 1000;
const ORDER_BUFFER_MS = 5 * 60 * 1000;

function formatTime(date: Date, timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  if (timezone) {
    options.timeZone = timezone;
  }
  return date.toLocaleTimeString('en-US', options);
}

function formatScheduledTimeWithDay(date: Date, timezone?: string): string {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  if (timezone) {
    timeOptions.timeZone = timezone;
  }
  const timeStr = date.toLocaleTimeString('en-US', timeOptions);
  
  if (isToday) {
    return `Today, ${timeStr}`;
  } else if (isTomorrow) {
    return `Tomorrow, ${timeStr}`;
  } else {
    const dayOptions: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    if (timezone) {
      dayOptions.timeZone = timezone;
    }
    const dayStr = date.toLocaleDateString('en-US', dayOptions);
    return `${dayStr}, ${timeStr}`;
  }
}

const TIME_CHECK_INTERVAL_MS = 30 * 1000;

export function useOrderAvailability({
  restaurantId,
  liveStatus,
  serviceType,
  timing,
  scheduledStart,
  orderingEnabled = true,
  menuFetchSuccess,
  menuFetchLoading,
  timezone,
}: UseOrderAvailabilityParams): OrderAvailabilityResult {
  const { data: delaysData, isLoading: delaysLoading } = useOrderDelays(
    orderingEnabled ? restaurantId : undefined
  );

  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTick(Date.now());
    }, TIME_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return useMemo(() => {
    const now = nowTick;
    
    const prepDelayMs = delaysData
      ? (serviceType === 'delivery' ? delaysData.deliverySeconds : delaysData.takeoutSeconds) * 1000
      : 15 * 60 * 1000;

    const expectedPickupTime = new Date(now + prepDelayMs + ORDER_BUFFER_MS);

    if (!orderingEnabled) {
      return {
        isAvailable: false,
        bannerType: 'none',
        bannerMessage: '',
        expectedPickupTime,
        isLoading: false,
      };
    }

    if (delaysLoading) {
      return {
        isAvailable: true,
        bannerType: 'none',
        bannerMessage: '',
        expectedPickupTime,
        isLoading: true,
      };
    }

    // Handle scheduled orders FIRST - they don't require liveStatus since
    // the scheduled time slot is already validated by the menu/target API
    if (timing === 'scheduled') {
      if (!scheduledStart) {
        return {
          isAvailable: false,
          bannerType: 'scheduled-unavailable',
          bannerMessage: 'Please select a pickup/delivery time',
          expectedPickupTime,
          isLoading: false,
        };
      }
      
      // Validate scheduled time is in the future
      const scheduledTimeMs = scheduledStart * 1000; // scheduledStart is in seconds
      if (scheduledTimeMs < now) {
        return {
          isAvailable: false,
          bannerType: 'scheduled-unavailable',
          bannerMessage: 'Selected time has passed. Please choose a new time.',
          expectedPickupTime: new Date(scheduledTimeMs),
          isLoading: false,
        };
      }
      
      // Optional checks against liveStatus if available - these are soft validations
      // since the scheduled time slot was pre-validated by the menu/target API
      if (liveStatus) {
        // If restaurant is currently closed and scheduled time is before next open, it might be invalid
        if (!liveStatus.open && liveStatus.nextOpenAt) {
          const nextOpenMs = liveStatus.nextOpenAt * 1000;
          if (scheduledTimeMs < nextOpenMs) {
            return {
              isAvailable: false,
              bannerType: 'scheduled-unavailable',
              bannerMessage: `Restaurant opens at ${formatTime(new Date(nextOpenMs), timezone)}. Please select a later time.`,
              expectedPickupTime: new Date(scheduledTimeMs),
              nextAvailableTime: new Date(nextOpenMs),
              isLoading: false,
            };
          }
        }
        
        // If restaurant is open but scheduled time is after close, log for awareness
        if (liveStatus.open && liveStatus.nextCloseAt) {
          const nextCloseMs = liveStatus.nextCloseAt * 1000;
          if (scheduledTimeMs > nextCloseMs) {
            // The API may have different hours for different days - this is soft validation only
            console.log('[Availability] Scheduled time after close, may need validation:', {
              scheduledTime: new Date(scheduledTimeMs),
              nextCloseAt: new Date(nextCloseMs),
            });
          }
        }
      }
      
      // Scheduled order with valid time - allow ordering and show scheduled context
      const scheduledDate = new Date(scheduledTimeMs);
      return {
        isAvailable: true,
        bannerType: 'scheduled',
        bannerMessage: `Scheduled for ${formatScheduledTimeWithDay(scheduledDate, timezone)}`,
        expectedPickupTime: scheduledDate,
        isLoading: false,
      };
    }

    // ASAP orders: availability is determined by menuFetchSuccess (menu/asap returned items)
    // This is the source of truth - if the endpoint returns items, the restaurant is available
    
    // If menu fetch is still loading, show loading state
    if (menuFetchLoading) {
      return {
        isAvailable: true, // Optimistically allow while loading
        bannerType: 'none',
        bannerMessage: '',
        expectedPickupTime,
        isLoading: true,
      };
    }
    
    // If menu fetch failed or returned no items, restaurant is not available for ASAP
    if (menuFetchSuccess === false) {
      return {
        isAvailable: false,
        bannerType: 'closed',
        bannerMessage: 'Not available for ASAP orders right now',
        expectedPickupTime,
        isLoading: false,
      };
    }
    
    // Menu fetch succeeded (has items) - restaurant IS available for ASAP
    // Use liveStatus only for informational banners (closes-soon, opens-soon) if available
    
    if (liveStatus) {
      const nextOpenAt = liveStatus.nextOpenAt ? liveStatus.nextOpenAt * 1000 : null;
      const nextCloseAt = liveStatus.nextCloseAt ? liveStatus.nextCloseAt * 1000 : null;
      
      // Show "opens soon" info banner if restaurant is technically closed but menu is available
      if (!liveStatus.open && nextOpenAt) {
        const timeUntilOpen = nextOpenAt - now;
        const opensInMinutes = Math.ceil(timeUntilOpen / 60000);
        
        if (timeUntilOpen <= OPENS_SOON_THRESHOLD_MS && expectedPickupTime.getTime() > nextOpenAt) {
          return {
            isAvailable: true, // Menu returned items, so it's available
            bannerType: 'opens-soon',
            bannerMessage: `Opens in ${opensInMinutes} min at ${formatTime(new Date(nextOpenAt), timezone)}`,
            nextAvailableTime: new Date(nextOpenAt),
            expectedPickupTime,
            isLoading: false,
          };
        }
      }
      
      // Show "closes soon" warning if applicable
      if (liveStatus.open && nextCloseAt) {
        const timeUntilClose = nextCloseAt - now;
        
        // Check if order might not be ready before close
        if (expectedPickupTime.getTime() > nextCloseAt) {
          return {
            isAvailable: false,
            bannerType: 'order-after-close',
            bannerMessage: `Restaurant closes at ${formatTime(new Date(nextCloseAt), timezone)} - order won't be ready in time`,
            nextAvailableTime: nextOpenAt ? new Date(nextOpenAt) : undefined,
            expectedPickupTime,
            isLoading: false,
          };
        }
        
        if (timeUntilClose <= CLOSES_SOON_THRESHOLD_MS) {
          return {
            isAvailable: true,
            bannerType: 'closes-soon',
            bannerMessage: `Closes soon at ${formatTime(new Date(nextCloseAt), timezone)}`,
            expectedPickupTime,
            isLoading: false,
          };
        }
      }
    }
    
    // Menu fetch succeeded and no warnings needed - fully available
    return {
      isAvailable: true,
      bannerType: 'none',
      bannerMessage: '',
      expectedPickupTime,
      isLoading: false,
    };
  }, [nowTick, liveStatus, serviceType, timing, scheduledStart, orderingEnabled, delaysData, delaysLoading, menuFetchSuccess, menuFetchLoading, timezone]);
}

function formatNextOpenDisplay(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  if (isToday) {
    return `today at ${formatTime(date)}`;
  } else if (isTomorrow) {
    return `tomorrow at ${formatTime(date)}`;
  } else {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    return `${dayName} at ${formatTime(date)}`;
  }
}
